import { format } from 'date-fns'
import { ProductAvailabilitySession } from '@big-red-group/storefront-common-checkout'
import { AvailabilityCalendarDate, CalendarOptionAvailabilityStatus, LastMinutePricingDate } from '@/types'

const unavailableBookingStatus: CalendarOptionAvailabilityStatus[] = ['SOLD_OUT', 'UNAVAILABLE']

export const formatBookingDate = (date: Date) => format(date ?? new Date(), 'yyyy-MM-dd')

export const formatDateAsShortMonthAndNumericDay = (date: Date) => {
  const formattedDate = new Date(date)
  return formattedDate.toLocaleDateString('en-us', {
    weekday: 'short',
    day: 'numeric',
    year: 'numeric',
    month: 'short',
  })
}

export const addDays = (date: Date, noOfDays: number) => {
  const newDate = date ? new Date(date) : new Date()
  newDate.setDate(date?.getDate() + noOfDays)
  return newDate
}

export const getNoAdjacentAvailabilityMessage = (bookingDate: Date): string => {
  return `Oh no! There is no availability on ${formatDateAsShortMonthAndNumericDay(
    bookingDate
  )}. We've checked adjacent dates and unfortunately there are no sessions between ${formatDateAsShortMonthAndNumericDay(
    addDays(bookingDate, 1)
  )} to ${formatDateAsShortMonthAndNumericDay(addDays(bookingDate, 3))} either. Please select a different date.`
}

export const getAdjacentAvailabilityMessage = (bookingDate: Date): string => {
  return `Oh no! There is no availability on the ${formatDateAsShortMonthAndNumericDay(
    bookingDate
  )}. Try some adjacent dates below or select a different date...`
}

export const getNoAvailabilityMessage = (bookingDate: Date): string => {
  return `Oh no! There is no availability on the ${formatDateAsShortMonthAndNumericDay(
    bookingDate
  )}. Try an adjacent date above or select a different date on the calendar.`
}

export const getHotDealText = (groupedSessions: ProductAvailabilitySession[], selectedSessionId?: string | number) => {
  if (groupedSessions.length > 1) {
    return groupedSessions.find((gs) => gs.id === selectedSessionId)?.hotDeal
  }
  return groupedSessions.at(0)!.hotDeal
}

export const getSessionSummary = (
  selectedSessionId?: string | number,
  selectedDisplayDate?: Date,
  productSessions?: ProductAvailabilitySession[]
): string => {
  if (!selectedSessionId || !selectedDisplayDate || !productSessions?.length) {
    return ''
  }
  const productSession = productSessions?.find(
    (session) =>
      (session.id || session.sessionKey) === selectedSessionId &&
      session.sessionDate === format(selectedDisplayDate, 'yyyy-MM-dd')
  )
  return productSession?.sessionName || ''
}

export const formatCalendarDate = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

export const getNextAvailableDate = (dates: AvailabilityCalendarDate[], afterDate?: Date): Date | null => {
  if (afterDate) {
    afterDate.setHours(0, 0, 0, 0)
  }

  for (const dateEntry of dates) {
    const date = new Date(dateEntry.date)
    date.setHours(0, 0, 0, 0)
    const status = dateEntry.productOptionsSummary.status as CalendarOptionAvailabilityStatus

    if ((!afterDate || date > afterDate) && !unavailableBookingStatus.includes(status)) {
      return date
    }
  }
  return null
}

export const getPreviousAvailableDate = (dates: AvailabilityCalendarDate[], beforeDate: Date): Date | null => {
  const targetDate = new Date(beforeDate)
  targetDate.setHours(0, 0, 0, 0)

  for (const dateEntry of dates) {
    const date = new Date(dateEntry.date)
    date.setHours(0, 0, 0, 0)

    if (date < targetDate) {
      const status = dateEntry.productOptionsSummary.status as CalendarOptionAvailabilityStatus
      if (!unavailableBookingStatus.includes(status)) {
        return date
      }
    }
  }
  return null
}
